import React, { useEffect, useState } from 'react';
import { FaTimes, FaWrench, FaMapMarkerAlt, FaCalendarAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import '../styles/InventoryEquipment.css';

const CREATE = `
  mutation CreateEquipment($createEquipmentInput: CreateEquipmentInput!) {
    createEquipment(createEquipmentInput: $createEquipmentInput) {
      id assetNumber
    }
  }
`;

const UPDATE = `
  mutation UpdateEquipment($updateEquipmentInput: UpdateEquipmentInput!) {
    updateEquipment(updateEquipmentInput: $updateEquipmentInput) {
      id assetNumber
    }
  }
`;

const TYPES = [
  'Vacuum',
  'Scrubber',
  'Extractor',
  'Pressure Washer',
  'Other',
];

const STATUSES = [
  { value: 'Active', label: 'Active' },
  { value: 'InUse', label: 'In Use' },
  { value: 'InService', label: 'In Service' },
  { value: 'ServiceOverdue', label: 'Service Overdue' },
];

function toDateInput(raw) {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function splitBrandModel(row) {
  if (row?.brand || row?.model) {
    return { brand: row.brand || '', model: row.model || '' };
  }
  const s = String(row?.brandModel || '').trim();
  if (!s) return { brand: '', model: '' };
  const parts = s.split(/\s+/);
  if (parts.length === 1) return { brand: parts[0], model: '' };
  return { brand: parts[0], model: parts.slice(1).join(' ') };
}

function emptyForm() {
  return {
    equipmentType: '',
    brand: '',
    model: '',
    serialNumber: '',
    purchaseDate: '',
    purchaseValue: '',
    currentLocation: '',
    currentSite: '',
    assignedTo: '',
    lastServiceDate: '',
    nextServiceDate: '',
    serviceFrequency: '',
    status: 'Active',
    notes: '',
  };
}

export default function EquipmentForm({
  onClose,
  onSaved,
  onDelete,
  editData = null,
  nextAssetNumber = 'ASS-001',
}) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (!editData) {
      setForm(emptyForm());
      return;
    }
    const names = splitBrandModel(editData);
    setForm({
      equipmentType: editData.equipmentType || '',
      brand: names.brand,
      model: names.model,
      serialNumber: editData.serialNumber || '',
      purchaseDate: toDateInput(editData.purchaseDate),
      purchaseValue:
        editData.purchaseValue != null ? String(editData.purchaseValue) : '',
      currentLocation: editData.currentLocation || '',
      currentSite: editData.currentSite || '',
      assignedTo: editData.assignedTo || '',
      lastServiceDate: toDateInput(editData.lastServiceDate),
      nextServiceDate: toDateInput(editData.nextServiceDate),
      serviceFrequency: editData.serviceFrequency || '',
      status: editData.status || 'Active',
      notes: editData.notes || '',
    });
  }, [editData]);

  const set = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.equipmentType.trim()) {
      toast.error('Equipment type is required');
      return;
    }
    if (!form.brand.trim() && !form.model.trim()) {
      toast.error('Brand or model is required');
      return;
    }
    if (!form.purchaseDate) {
      toast.error('Purchase date is required');
      return;
    }
    if (form.purchaseValue === '' || Number.isNaN(Number(form.purchaseValue))) {
      toast.error('Purchase value is required');
      return;
    }
    const payload = {
      equipmentType: form.equipmentType.trim(),
      brand: form.brand.trim() || undefined,
      model: form.model.trim() || undefined,
      serialNumber: form.serialNumber.trim() || undefined,
      purchaseDate: form.purchaseDate || undefined,
      purchaseValue: Number.parseFloat(form.purchaseValue),
      currentLocation: form.currentLocation.trim() || undefined,
      currentSite: form.currentSite.trim() || undefined,
      assignedTo: form.assignedTo.trim() || undefined,
      lastServiceDate: form.lastServiceDate || undefined,
      nextServiceDate: form.nextServiceDate || undefined,
      serviceFrequency: form.serviceFrequency.trim() || undefined,
      notes: form.notes.trim() || undefined,
    };
    if (editData?.id) {
      payload.status = form.status || undefined;
    }

    setLoading(true);
    try {
      if (editData?.id) {
        await graphqlRequest(UPDATE, {
          updateEquipmentInput: { id: editData.id, ...payload },
        });
        toast.success('Equipment updated');
      } else {
        await graphqlRequest(CREATE, { createEquipmentInput: payload });
        toast.success('Equipment added');
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.message || 'Failed to save equipment');
    } finally {
      setLoading(false);
    }
  };

  const assetNumber = editData?.assetNumber || nextAssetNumber;

  return (
    <div className="ieq-overlay" role="dialog" aria-modal="true">
      <div className="ieq-modal ieq-modal--form">
        <header className="ieq-modal-header">
          <h2>
            {editData
              ? `Edit Equipment — ${editData.assetNumber || ''}`
              : 'Add Equipment'}
          </h2>
          <button
            type="button"
            className="ieq-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </header>
        <form className="ieq-modal-body" onSubmit={handleSubmit}>
          <section className="ieq-section">
            <h3 className="ieq-section-title">
              <FaWrench /> Asset Information
            </h3>
            <div className="ieq-grid">
              <label className="ieq-field">
                <span>Asset Number</span>
                <input value={assetNumber} disabled />
              </label>
              <label className="ieq-field">
                <span>
                  Equipment Type <em>*</em>
                </span>
                <select
                  value={form.equipmentType}
                  onChange={set('equipmentType')}
                  required
                >
                  <option value="">Select type</option>
                  {TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </label>
              <label className="ieq-field">
                <span>Brand</span>
                <input
                  value={form.brand}
                  onChange={set('brand')}
                  placeholder="e.g. Nilfisk"
                />
              </label>
              <label className="ieq-field">
                <span>Model</span>
                <input
                  value={form.model}
                  onChange={set('model')}
                  placeholder="e.g. GD930"
                />
              </label>
              <label className="ieq-field">
                <span>Serial Number</span>
                <input
                  value={form.serialNumber}
                  onChange={set('serialNumber')}
                />
              </label>
              <label className="ieq-field">
                <span>
                  Purchase Date <em>*</em>
                </span>
                <input
                  type="date"
                  value={form.purchaseDate}
                  onChange={set('purchaseDate')}
                  required
                />
              </label>
              <label className="ieq-field">
                <span>
                  Purchase Value <em>*</em>
                </span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.purchaseValue}
                  onChange={set('purchaseValue')}
                  placeholder="0.00"
                  required
                />
              </label>
            </div>
          </section>

          <section className="ieq-section">
            <h3 className="ieq-section-title">
              <FaMapMarkerAlt /> Assignment
            </h3>
            <div className="ieq-grid">
              <label className="ieq-field">
                <span>Current Location</span>
                <input
                  value={form.currentLocation}
                  onChange={set('currentLocation')}
                  placeholder="Warehouse / van / site"
                />
              </label>
              <label className="ieq-field">
                <span>Assigned Site</span>
                <input
                  value={form.currentSite}
                  onChange={set('currentSite')}
                  placeholder="Site name"
                />
              </label>
              <label className="ieq-field">
                <span>Assigned Employee</span>
                <input
                  value={form.assignedTo}
                  onChange={set('assignedTo')}
                  placeholder="Staff name"
                />
              </label>
              {editData ? (
                <label className="ieq-field">
                  <span>Status</span>
                  <select value={form.status} onChange={set('status')}>
                    {STATUSES.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
            </div>
          </section>

          <section className="ieq-section">
            <h3 className="ieq-section-title">
              <FaCalendarAlt /> Service
            </h3>
            <div className="ieq-grid">
              <label className="ieq-field">
                <span>Last Service Date</span>
                <input
                  type="date"
                  value={form.lastServiceDate}
                  onChange={set('lastServiceDate')}
                />
              </label>
              <label className="ieq-field">
                <span>Next Service Due</span>
                <input
                  type="date"
                  value={form.nextServiceDate}
                  onChange={set('nextServiceDate')}
                />
              </label>
              <label className="ieq-field">
                <span>Service Frequency</span>
                <input
                  value={form.serviceFrequency}
                  onChange={set('serviceFrequency')}
                  placeholder="e.g. Every 3 months"
                />
              </label>
              <label className="ieq-field ieq-field--full">
                <span>Notes</span>
                <textarea
                  rows={3}
                  value={form.notes}
                  onChange={set('notes')}
                />
              </label>
            </div>
          </section>

          <div className="ieq-modal-footer">
            {editData && onDelete ? (
              <button
                type="button"
                className="ieq-btn ieq-btn--danger-text"
                onClick={() => onDelete(editData)}
              >
                Delete
              </button>
            ) : (
              <span />
            )}
            <button type="button" className="ieq-btn ieq-btn--ghost" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="ieq-btn ieq-btn--primary"
              disabled={loading}
            >
              {loading ? 'Saving…' : editData ? 'Save Changes' : 'Add Equipment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
